// A3 — Causales de paro (jerarquía 2 niveles: madre → específica).
// Las categorías son configurables por tenant (tabla `categorias_paro`).

import { useState } from 'react';
import { toast } from 'sonner';
import type { AdminCausal, CategoriaParoOutput } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { AdminSidebar } from '../../components/layout/AdminSidebar';
import {
  useAdminCausales,
  useCreateAdminCausal,
  useUpdateAdminCausal,
  useAdminCategoriasParo,
  useCreateAdminCategoriaParo,
  useUpdateAdminCategoriaParo,
} from '../../lib/api/queries';
import { useMarcarCambioReferenciaCategoria } from '../../lib/api/ingesta';

// MadreCtx ahora referencia la categoría por UUID, no por enum.
type MadreCtx = { id: string; categoria_id: string };

export function A3CausalesPage() {
  const { data, isLoading, isError } = useAdminCausales();
  const { data: categorias = [] } = useAdminCategoriasParo();

  const [crearMadre, setCrearMadre] = useState(false);
  const [crearHija, setCrearHija] = useState<MadreCtx | null>(null);
  const [editar, setEditar] = useState<AdminCausal | null>(null);
  const [gestionCategorias, setGestionCategorias] = useState(false);
  const [editarCategoria, setEditarCategoria] = useState<CategoriaParoOutput | null>(null);

  // Helper: dada una categoria_id, devuelve el objeto CategoriaParoOutput o null.
  const catById = (id: string) => categorias.find((c) => c.id === id) ?? null;

  return (
    <RoleLayout sidebar={<AdminSidebar />} roleLabel="Admin del Tenant">
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-medium text-fg">Causales de paro</h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">Jerarquía 2 niveles · tenant-wide</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setGestionCategorias(true)}>
              Categorías
            </Button>
            <Button onClick={() => setCrearMadre(true)}>Nueva causal madre</Button>
          </div>
        </header>
        {isLoading || !data ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            {isError ? <span className="text-sm text-crit">No se pudo cargar.</span> : <Spinner />}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {data.causales.map((m) => {
              const cat = catById(m.categoria_id);
              return (
                <Card key={m.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-fg">{m.nombre}</span>
                        <span className="text-[11px] text-fg-tertiary">{m.codigo}</span>
                        {cat ? (
                          <span
                            className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium text-white"
                            style={{ backgroundColor: cat.color ?? 'var(--color-fg-tertiary)' }}
                          >
                            {cat.nombre}
                          </span>
                        ) : (
                          <Badge tone="neutral">Sin categoría</Badge>
                        )}
                        {!m.activa && <Badge tone="neutral">Inactiva</Badge>}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setCrearHija({ id: m.id, categoria_id: m.categoria_id })}
                        >
                          + Hija
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditar(m)}>
                          Editar
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-col divide-y divide-border border-t border-border">
                      {m.hijas.map((h) => (
                        <div key={h.id} className="flex items-center justify-between py-1.5 pl-4">
                          <span className="flex items-center gap-2 text-sm text-fg-secondary">
                            {h.nombre}
                            <span className="text-[10px] text-fg-tertiary">{h.codigo}</span>
                            {h.requiere_observacion && <Badge tone="info">Requiere obs.</Badge>}
                            {!h.activa && <Badge tone="neutral">Inactiva</Badge>}
                          </span>
                          <Button size="sm" variant="ghost" onClick={() => setEditar(h)}>
                            Editar
                          </Button>
                        </div>
                      ))}
                      {m.hijas.length === 0 && (
                        <span className="py-1.5 pl-4 text-xs text-fg-tertiary">
                          Sin causales específicas
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      {crearMadre && <CrearDialog categorias={categorias} onClose={() => setCrearMadre(false)} />}
      {crearHija && (
        <CrearDialog madre={crearHija} categorias={categorias} onClose={() => setCrearHija(null)} />
      )}
      {editar && <EditarDialog causal={editar} onClose={() => setEditar(null)} />}
      {gestionCategorias && (
        <GestionCategoriasDialog
          categorias={categorias}
          onEditar={(c) => {
            setGestionCategorias(false);
            setEditarCategoria(c);
          }}
          onClose={() => setGestionCategorias(false)}
        />
      )}
      {editarCategoria && (
        <CategoriaDialog categoria={editarCategoria} onClose={() => setEditarCategoria(null)} />
      )}
    </RoleLayout>
  );
}

// =============================================================================
// Dialog: Crear causal (madre o hija)
// =============================================================================

function CrearDialog({
  madre,
  categorias,
  onClose,
}: {
  madre?: MadreCtx;
  categorias: CategoriaParoOutput[];
  onClose: () => void;
}) {
  const crear = useCreateAdminCausal();
  const primeraCategoria = categorias.find((c) => c.activa)?.id ?? categorias[0]?.id ?? '';
  const [f, setF] = useState({
    codigo: '',
    nombre: '',
    categoria_id: madre?.categoria_id ?? primeraCategoria,
    requiere_observacion: false,
  });

  return (
    <Dialog open onClose={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{madre ? 'Nueva causal específica' : 'Nueva causal madre'}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label>Código</Label>
            <Input value={f.codigo} onChange={(e) => setF({ ...f, codigo: e.target.value })} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Nombre</Label>
            <Input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} />
          </div>
          {!madre && (
            <div className="flex flex-col gap-1">
              <Label>Categoría</Label>
              {categorias.length === 0 ? (
                <p className="text-xs text-fg-tertiary">
                  No hay categorías configuradas. Creá una primero.
                </p>
              ) : (
                <Select
                  value={f.categoria_id}
                  onChange={(e) => setF({ ...f, categoria_id: e.target.value })}
                >
                  {categorias
                    .filter((c) => c.activa)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nombre}
                      </option>
                    ))}
                </Select>
              )}
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={f.requiere_observacion}
              onChange={(e) => setF({ ...f, requiere_observacion: e.target.checked })}
            />
            Requiere observación
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={
              crear.isPending || !f.codigo.trim() || !f.nombre.trim() || (!madre && !f.categoria_id)
            }
            onClick={() =>
              crear.mutate(
                {
                  parent_id: madre?.id ?? null,
                  categoria_id: madre ? madre.categoria_id : f.categoria_id,
                  codigo: f.codigo.trim(),
                  nombre: f.nombre.trim(),
                  requiere_observacion: f.requiere_observacion,
                },
                {
                  onSuccess: () => {
                    toast.success('Causal creada');
                    onClose();
                  },
                  onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error'),
                },
              )
            }
          >
            Crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Dialog: Editar causal existente
// =============================================================================

function EditarDialog({ causal, onClose }: { causal: AdminCausal; onClose: () => void }) {
  const update = useUpdateAdminCausal();
  const [f, setF] = useState({
    nombre: causal.nombre,
    requiere_observacion: causal.requiere_observacion,
    activa: causal.activa,
    razon: '',
  });
  const desactivando = causal.activa && !f.activa;
  return (
    <Dialog open onClose={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar {causal.nombre}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label>Nombre</Label>
            <Input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={f.requiere_observacion}
              onChange={(e) => setF({ ...f, requiere_observacion: e.target.checked })}
            />
            Requiere observación
          </label>
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={f.activa}
              onChange={(e) => setF({ ...f, activa: e.target.checked })}
            />
            Activa
          </label>
          {desactivando && (
            <div className="flex flex-col gap-1">
              <Label>Razón de desactivación (≥20)</Label>
              <Input value={f.razon} onChange={(e) => setF({ ...f, razon: e.target.value })} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={update.isPending || (desactivando && f.razon.trim().length < 20)}
            onClick={() =>
              update.mutate(
                {
                  id: causal.id,
                  body: {
                    nombre: f.nombre.trim(),
                    requiere_observacion: f.requiere_observacion,
                    activa: f.activa,
                    ...(desactivando ? { razon: f.razon.trim() } : {}),
                  },
                },
                {
                  onSuccess: () => {
                    toast.success('Causal actualizada');
                    onClose();
                  },
                  onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error'),
                },
              )
            }
          >
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Dialog: Gestión de categorías (listado + botón nueva)
// =============================================================================

function GestionCategoriasDialog({
  categorias,
  onEditar,
  onClose,
}: {
  categorias: CategoriaParoOutput[];
  onEditar: (c: CategoriaParoOutput) => void;
  onClose: () => void;
}) {
  const [nuevaCategoria, setNuevaCategoria] = useState(false);
  return (
    <Dialog open onClose={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Categorías de paro</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {categorias.length === 0 ? (
            <p className="py-4 text-center text-sm text-fg-tertiary">
              No hay categorías configuradas.
            </p>
          ) : (
            <div className="flex flex-col divide-y divide-border rounded-md border border-border">
              {categorias.map((c) => (
                <div key={c.id} className="flex items-center justify-between px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block size-3 rounded-sm"
                      style={{ backgroundColor: c.color ?? 'var(--color-fg-tertiary)' }}
                    />
                    <span className="text-sm text-fg">{c.nombre}</span>
                    <span className="text-[10px] text-fg-tertiary">{c.codigo}</span>
                    {!c.activa && <Badge tone="neutral">Inactiva</Badge>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => onEditar(c)}>
                    Editar
                  </Button>
                </div>
              ))}
            </div>
          )}
          <Button onClick={() => setNuevaCategoria(true)}>Nueva categoría</Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
      {nuevaCategoria && <CategoriaDialog onClose={() => setNuevaCategoria(false)} />}
    </Dialog>
  );
}

// =============================================================================
// Dialog: Crear / editar una categoría de paro
// =============================================================================

const HEX_RE = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

function CategoriaDialog({
  categoria,
  onClose,
}: {
  categoria?: CategoriaParoOutput;
  onClose: () => void;
}) {
  const crear = useCreateAdminCategoriaParo();
  const actualizar = useUpdateAdminCategoriaParo();
  const marcarCambioRef = useMarcarCambioReferenciaCategoria();
  const isPending = crear.isPending || actualizar.isPending || marcarCambioRef.isPending;
  // Marca de cambio de referencia (integración con máquinas). El API no la
  // expone en lectura: '' = no tocarla; 'si'/'no' se envían al guardar.
  const [cambioRef, setCambioRef] = useState<'' | 'si' | 'no'>('');

  const [f, setF] = useState({
    codigo: categoria?.codigo ?? '',
    nombre: categoria?.nombre ?? '',
    color: categoria?.color ?? '#6B7280',
    afecta_disponibilidad: categoria?.afecta_disponibilidad ?? false,
    activa: categoria?.activa ?? true,
  });

  const colorValido = !f.color || HEX_RE.test(f.color);

  const handleGuardar = () => {
    if (categoria) {
      actualizar.mutate(
        {
          id: categoria.id,
          body: {
            nombre: f.nombre.trim() || undefined,
            color: f.color || null,
            afecta_disponibilidad: f.afecta_disponibilidad,
            activa: f.activa,
          },
        },
        {
          onSuccess: () => {
            if (!cambioRef) {
              toast.success('Categoría actualizada');
              onClose();
              return;
            }
            marcarCambioRef.mutate(
              { id: categoria.id, body: { es_cambio_referencia: cambioRef === 'si' } },
              {
                onSuccess: () => {
                  toast.success('Categoría actualizada');
                  onClose();
                },
                onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error'),
              },
            );
          },
          onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error'),
        },
      );
    } else {
      crear.mutate(
        {
          codigo: f.codigo.trim(),
          nombre: f.nombre.trim(),
          color: f.color || null,
          afecta_disponibilidad: f.afecta_disponibilidad,
        },
        {
          onSuccess: () => {
            toast.success('Categoría creada');
            onClose();
          },
          onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error'),
        },
      );
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{categoria ? `Editar ${categoria.nombre}` : 'Nueva categoría'}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {!categoria && (
            <div className="flex flex-col gap-1">
              <Label>Código</Label>
              <Input value={f.codigo} onChange={(e) => setF({ ...f, codigo: e.target.value })} />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <Label>Nombre</Label>
            <Input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Color</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={HEX_RE.test(f.color) ? f.color : '#6B7280'}
                onChange={(e) => setF({ ...f, color: e.target.value })}
                className="h-8 w-10 cursor-pointer rounded border border-border bg-transparent"
              />
              <Input
                value={f.color}
                placeholder="#RRGGBB"
                onChange={(e) => setF({ ...f, color: e.target.value })}
                className={!colorValido ? 'border-crit' : ''}
              />
            </div>
            {!colorValido && (
              <span className="text-[11px] text-crit">
                Debe ser un color hex válido (#RGB o #RRGGBB)
              </span>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={f.afecta_disponibilidad}
              onChange={(e) => setF({ ...f, afecta_disponibilidad: e.target.checked })}
            />
            Afecta disponibilidad
          </label>
          {categoria && (
            <label className="flex items-center gap-2 text-sm text-fg">
              <input
                type="checkbox"
                checked={f.activa}
                onChange={(e) => setF({ ...f, activa: e.target.checked })}
              />
              Activa
            </label>
          )}
          {categoria && (
            <div className="flex flex-col gap-1">
              <Label htmlFor="categoria-cambio-ref">Cambio de referencia</Label>
              <Select
                id="categoria-cambio-ref"
                value={cambioRef}
                onChange={(e) => setCambioRef(e.target.value as '' | 'si' | 'no')}
              >
                <option value="">Sin cambios</option>
                <option value="si">Sí: sus paros cuentan como cambio de referencia</option>
                <option value="no">No es cambio de referencia</option>
              </Select>
              <span className="text-[11px] text-fg-tertiary">
                Los paros de esta categoría se agrupan en el análisis de cambios de referencia.
              </span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={
              isPending || !f.nombre.trim() || (!categoria && !f.codigo.trim()) || !colorValido
            }
            onClick={handleGuardar}
          >
            {categoria ? 'Guardar' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
